import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { listActivePermits, type PermitRow } from '../lib/db';

/**
 * Dashboard widget that surfaces active permits needing attention:
 *   - Expired but not closed out (RED)
 *   - Hot-works in fire-watch period after expiry (AMBER)
 *   - Expiring within the next 2 hours (AMBER)
 *
 * Sits between the alerts banner and the tile grid on the Dashboard.
 * Hidden when there are no permits in any of the above states — so it
 * doesn't clutter the home screen when everything is quiet.
 *
 * Why this is its own widget and not just another dashboard alert:
 *   - Per-permit context (title, time remaining, fire-watch status) matters
 *     more than a single "N permits expired" summary line.
 *   - One-tap to the permit view where the proper close-out form lives
 *     (completion checks + closing signature). We do NOT close from here
 *     directly — the per-template completion checks are non-negotiable.
 */

interface PermitWithUrgency extends PermitRow {
  urgency: 'expired' | 'fire-watch' | 'expiring-soon';
  minutesToExpiry: number; // negative if already expired
  fireWatchEndsAt?: number; // ms epoch — only for hot_works
}

function classify(p: PermitRow, now: number): PermitWithUrgency | null {
  const expiresMs = new Date(p.expires_at).getTime();
  const minutesToExpiry = Math.round((expiresMs - now) / 60000);

  // Already past expiry, not closed out → RED
  if (minutesToExpiry < 0) {
    // Hot-works: if still inside the fire-watch window, AMBER not RED
    if (p.permit_type === 'hot_works' && p.fire_watch_minutes) {
      const fireWatchEndsAt = expiresMs + p.fire_watch_minutes * 60_000;
      if (fireWatchEndsAt > now) {
        return { ...p, urgency: 'fire-watch', minutesToExpiry, fireWatchEndsAt };
      }
    }
    return { ...p, urgency: 'expired', minutesToExpiry };
  }
  // Expiring within the next 2 hours → AMBER
  if (minutesToExpiry <= 120) {
    return { ...p, urgency: 'expiring-soon', minutesToExpiry };
  }
  return null;
}

function formatRelative(minutes: number): string {
  const abs = Math.abs(minutes);
  if (abs < 60) return `${abs} min`;
  const hours = Math.floor(abs / 60);
  const mins = abs % 60;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}

interface Props {
  siteId: string | null | undefined;
}

export function PermitsCloseOutWidget({ siteId }: Props) {
  const [permits, setPermits] = useState<PermitWithUrgency[]>([]);
  const [, setTick] = useState(0);

  // Poll for permits every 60s so countdowns + new permits land without a refresh.
  useEffect(() => {
    if (!siteId) { setPermits([]); return; }
    let cancelled = false;
    async function refresh() {
      try {
        const rows = await listActivePermits(siteId!);
        const now = Date.now();
        const flagged = rows
          .map((p) => classify(p, now))
          .filter((p): p is PermitWithUrgency => p !== null)
          // RED first, then AMBER. Within a severity, soonest to expire first.
          .sort((a, b) => {
            const rank = { expired: 0, 'fire-watch': 1, 'expiring-soon': 2 };
            const r = rank[a.urgency] - rank[b.urgency];
            if (r !== 0) return r;
            return a.minutesToExpiry - b.minutesToExpiry;
          });
        if (!cancelled) setPermits(flagged);
      } catch {
        if (!cancelled) setPermits([]);
      }
    }
    void refresh();
    const t = window.setInterval(refresh, 60_000);
    return () => { cancelled = true; window.clearInterval(t); };
  }, [siteId]);

  // Re-render every 30s so the relative-time labels stay fresh between
  // network polls. (The poll happens every 60s; this gives mid-cycle ticks.)
  useEffect(() => {
    const t = window.setInterval(() => setTick((n) => n + 1), 30_000);
    return () => window.clearInterval(t);
  }, []);

  if (permits.length === 0) return null;

  return (
    <div
      className="permits-closeout-widget"
      style={{
        background: '#FFFFFF',
        border: '1px solid #E2E8F0',
        borderRadius: 12,
        padding: '14px 16px',
        margin: '0 0 16px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <strong style={{ fontSize: 14, color: '#0F172A' }}>
          Permits — close-out needed
        </strong>
        <span style={{ fontSize: 12, color: '#64748B' }}>
          {permits.length} {permits.length === 1 ? 'permit' : 'permits'}
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {permits.map((p) => {
          const isExpired = p.urgency === 'expired';
          const isFireWatch = p.urgency === 'fire-watch';
          const bg = isExpired ? '#FEF2F2' : '#FFFBEB';
          const border = isExpired ? '#FECACA' : '#FDE68A';
          const dot = isExpired ? '🔴' : '🟡';

          let label: string;
          if (isExpired) {
            label = `expired ${formatRelative(p.minutesToExpiry)} ago`;
          } else if (isFireWatch && p.fireWatchEndsAt) {
            const fwLeft = Math.round((p.fireWatchEndsAt - Date.now()) / 60000);
            label = `fire watch — ${formatRelative(fwLeft)} left`;
          } else {
            label = `expires in ${formatRelative(p.minutesToExpiry)}`;
          }

          return (
            <Link
              key={p.id}
              to={`/permits/${p.id}`}
              className="permit-closeout-row"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 12px',
                background: bg,
                border: `1px solid ${border}`,
                borderRadius: 8,
                textDecoration: 'none',
                color: '#0F172A',
              }}
            >
              <span aria-hidden style={{ fontSize: 14 }}>{dot}</span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {p.title}
                </span>
                <span style={{ display: 'block', fontSize: 12, color: '#475569' }}>
                  {label}
                </span>
              </span>
              <span style={{ fontSize: 13, fontWeight: 600, color: isExpired ? '#B91C1C' : '#B45309' }}>
                Close out →
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
